import { useTranslation } from "react-i18next"

import type { CustomerConfig } from "@/types/customer-config"

import customerConfig from "@/customer/config.json"
import { getInjectedOrganizationName } from "@/lib/runtime-branding"
import { cn } from "@/lib/utils"

type ComplianceLevel = "compliant" | "partial" | "non_compliant" | "neutral"

const COMPLIANCE_STYLES: Record<ComplianceLevel, string> = {
  compliant: "bg-success/15 text-success",
  partial: "bg-warning/15 text-warning",
  non_compliant: "bg-destructive/15 text-destructive",
  neutral: "bg-muted/50",
}

type RowData = {
  label: string
  chatgpt: { text: string; level: ComplianceLevel }
  copilot: { text: string; level: ComplianceLevel }
  app: { text: string; level: ComplianceLevel }
}

export function ToolsComparisonTable() {
  const { t } = useTranslation("", { keyPrefix: "training.components.tools_comparison" })

  const rows: RowData[] = [
    {
      label: t("rows.vendor", "Vendor"),
      chatgpt: { text: "OpenAI", level: "neutral" },
      copilot: { text: "Microsoft", level: "neutral" },
      app: {
        text:
          getInjectedOrganizationName() ||
          (customerConfig as CustomerConfig).config.organization_name ||
          t("rows.vendor_app", "Your organization"),
        level: "neutral",
      },
    },
    {
      label: t("rows.purpose", "Purpose"),
      chatgpt: {
        text: t("rows.purpose_chatgpt", "General openness to purpose"),
        level: "compliant",
      },
      copilot: {
        text: t("rows.purpose_copilot", "Productivity within M365 tools"),
        level: "partial",
      },
      app: {
        text: t("rows.purpose_app", "General openness to purpose"),
        level: "compliant",
      },
    },
    {
      label: t("rows.flexibility", "Function Flexibility"),
      chatgpt: {
        text: t("rows.flexibility_chatgpt", "Dependent on OpenAI's product roadmap"),
        level: "partial",
      },
      copilot: {
        text: t("rows.flexibility_copilot", "Dependent on Microsoft product roadmap"),
        level: "partial",
      },
      app: {
        text: t("rows.flexibility_app", "Full flexibility (custom development)"),
        level: "compliant",
      },
    },
    {
      label: t("rows.infrastructure", "Infrastructure"),
      chatgpt: {
        text: t("rows.infra_chatgpt", "Third-party infrastructure (US)"),
        level: "non_compliant",
      },
      copilot: {
        text: t("rows.infra_copilot", "M365 Cloud Infrastructure (EU)"),
        level: "compliant",
      },
      app: {
        text: t("rows.infra_app", "Own Azure Cloud Infrastructure (EU)"),
        level: "compliant",
      },
    },
    {
      label: t("rows.llm", "Underlying LLM"),
      chatgpt: {
        text: t("rows.llm_chatgpt", "OpenAI models"),
        level: "non_compliant",
      },
      copilot: {
        text: t("rows.llm_copilot", "OpenAI models (M365 hosted, EU)"),
        level: "compliant",
      },
      app: {
        text: t("rows.llm_app", "OpenAI models and more (Azure hosted, EU)"),
        level: "compliant",
      },
    },
  ]

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-muted-foreground p-3 text-left font-medium">
              {t("header.feature", "Feature")}
            </th>
            <th className="p-3 text-left font-semibold">ChatGPT</th>
            <th className="p-3 text-left font-semibold">M365 Copilot</th>
            <th className="text-primary p-3 text-left font-semibold">
              {t("header.app", "{{appName}}")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-b last:border-0">
              <td className="text-muted-foreground p-3 font-medium">{row.label}</td>
              <td className={cn("p-3", COMPLIANCE_STYLES[row.chatgpt.level])}>
                {row.chatgpt.text}
              </td>
              <td className={cn("p-3", COMPLIANCE_STYLES[row.copilot.level])}>
                {row.copilot.text}
              </td>
              <td className={cn("p-3", COMPLIANCE_STYLES[row.app.level])}>{row.app.text}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 flex flex-wrap gap-4 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="bg-success border-foreground inline-block h-3 w-3 rounded-sm border" />
          {t("legend.compliant", "Compliant / fit to purpose")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="bg-warning border-foreground inline-block h-3 w-3 rounded-sm border" />
          {t("legend.partial", "Partly compliant / fit to purpose")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="bg-destructive border-foreground inline-block h-3 w-3 rounded-sm border" />
          {t("legend.non_compliant", "Not compliant")}
        </span>
      </div>
    </div>
  )
}
